import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

/**
 * One accent colour carries all interactive meaning (design-system skill).
 * Filled buttons get the faint shadow-button lift; secondary keeps a border
 * because a control needs an edge, not an elevation.
 */
const button = cva(
  "inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap " +
    // `colors, transform` rather than colours alone: the press below moves it,
    // and an untransitioned transform snaps.
    "transition-[colors,transform,box-shadow] duration-instant ease-out " +
    // A press you can feel. Two percent is under the threshold where it reads
    // as an animation and over the one where it reads as nothing -- the button
    // acknowledges the finger before the network does.
    "active:scale-[0.98] " +
    "disabled:pointer-events-none disabled:opacity-55 " +
    "[&_svg]:size-[1.125rem] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-ink shadow-button hover:bg-primary-hover active:bg-primary-active",
        secondary:
          "border border-line-strong bg-surface text-ink hover:bg-surface-hover active:bg-surface-active",
        ghost: "text-ink-muted hover:bg-surface-hover hover:text-ink",
        danger: "bg-danger text-danger-ink shadow-button hover:bg-danger-hover",
      },
      size: {
        sm: "h-control-sm px-3 text-sm",
        md: "h-control-md px-5 text-base",
        lg: "h-control-lg px-6 text-md",
        icon: "h-control-md w-control-md",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

/**
 * The classes alone, for a link that must look like a button -- "Open the
 * PDF" is a real <a>, so it can be opened in a new tab.
 */
export const buttonVariants = button;

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(button({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = "Button";
